import type {
  AnalyticsProduct,
  FeedbackProduct,
} from '@hotjar/webapp/domains/common/product/types';
import { getProductSkusByPlanId } from '@hotjar/webapp/domains/common/product/utils';
import type { Site } from '@hotjar/site';
import { useProductCatalog } from './useProductCatalog';

interface SiteProducts {
  feedbackProduct: FeedbackProduct | undefined;
  analyticsProduct: AnalyticsProduct | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * This hook will return the  products for a site.
 */
export const useGetSiteProducts = (site: Site): SiteProducts => {
  const { data: productCatalog, isLoading, isError } = useProductCatalog();

  if (!site) {
    return {
      isLoading,
      isError,
      analyticsProduct: undefined,
      feedbackProduct: undefined,
    };
  }

  const { planId } = site;
  const [analyticsSku, feedbackSku] = getProductSkusByPlanId(planId);

  return {
    isLoading,
    isError,
    feedbackProduct: productCatalog?.feedbackProducts.find((prod) => prod.sku === feedbackSku),
    analyticsProduct: productCatalog?.analyticsProducts.find((prod) => prod.sku === analyticsSku),
  };
};

interface EffectiveSiteProducts {
  effectiveFeedbackProduct: FeedbackProduct | undefined;
  effectiveAnalyticsProduct: AnalyticsProduct | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * This hook will return the effective products for a site.
 */
export const useGetSiteEffectiveProducts = (site: Site | undefined): EffectiveSiteProducts => {
  const { data: productCatalog, isLoading, isError } = useProductCatalog();

  if (!site) {
    return {
      effectiveAnalyticsProduct: undefined,
      effectiveFeedbackProduct: undefined,
      isLoading,
      isError,
    };
  }

  const { effectivePlanId } = site;
  const [analyticsSku, feedbackSku] = getProductSkusByPlanId(effectivePlanId);

  return {
    effectiveFeedbackProduct: productCatalog?.feedbackProducts.find(
      (prod) => prod.sku === feedbackSku,
    ),
    effectiveAnalyticsProduct: productCatalog?.analyticsProducts.find(
      (prod) => prod.sku === analyticsSku,
    ),
    isLoading,
    isError,
  };
};
